/**
 * RoomForm — create/edit a Room. Used inside branch detail and rooms list.
 * branchId is either fixed (from branch detail) or selectable (rooms list, admin).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import type { Branch } from '@client/lib/queries/use-branches';

const roomFormSchema = z.object({
  branchId: z.string().min(1, 'Chọn cơ sở'),
  name: z.string().min(1, 'Bắt buộc').max(100),
  capacity: z.coerce.number().int().positive('Sức chứa phải ≥ 1'),
  isActive: z.boolean(),
  note: z.string().optional().or(z.literal('')),
});

export type RoomFormValues = z.infer<typeof roomFormSchema>;

interface RoomFormProps {
  defaultValues?: Partial<RoomFormValues>;
  isEdit?: boolean;
  isSubmitting?: boolean;
  /** If provided, branchId field is locked to this branch */
  fixedBranchId?: string;
  /** Branch list for admin selector */
  branches?: Branch[];
  onSubmit: (values: RoomFormValues) => void;
  onCancel: () => void;
}

export function RoomForm({
  defaultValues,
  isEdit,
  isSubmitting,
  fixedBranchId,
  branches,
  onSubmit,
  onCancel,
}: RoomFormProps) {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<RoomFormValues>({
    resolver: zodResolver(roomFormSchema),
    defaultValues: {
      branchId: fixedBranchId ?? '',
      isActive: true,
      capacity: 1,
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset({
      branchId: fixedBranchId ?? '',
      isActive: true,
      capacity: 1,
      ...defaultValues,
    });
  }, [defaultValues, fixedBranchId, reset]);

  const isActiveVal = watch('isActive');
  const branchIdVal = watch('branchId');

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      {/* Branch selector — hidden when fixed */}
      {!fixedBranchId && (
        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">
            Cơ sở <span className="text-destructive">*</span>
          </Label>
          <Select
            value={branchIdVal}
            onValueChange={(v) => setValue('branchId', v)}
          >
            <SelectTrigger className="h-10">
              <SelectValue placeholder="Chọn cơ sở" />
            </SelectTrigger>
            <SelectContent>
              {(branches ?? []).map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.branchId && (
            <p className="text-xs text-destructive">{errors.branchId.message}</p>
          )}
        </div>
      )}

      {/* Room name */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="room-name" className="text-sm font-medium">
          Tên phòng <span className="text-destructive">*</span>
        </Label>
        <Input
          id="room-name"
          className="h-10 font-mono"
          placeholder="P.201"
          {...register('name')}
        />
        {errors.name && (
          <p className="text-xs text-destructive">{errors.name.message}</p>
        )}
      </div>

      {/* Capacity + isActive */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="capacity" className="text-sm font-medium">
            Sức chứa <span className="text-destructive">*</span>
          </Label>
          <Input
            id="capacity"
            type="number"
            min={1}
            className="h-10"
            {...register('capacity')}
          />
          {errors.capacity && (
            <p className="text-xs text-destructive">{errors.capacity.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-medium">Còn sử dụng</Label>
          <div className="flex items-center h-10 gap-3">
            <Switch
              checked={isActiveVal}
              onCheckedChange={(v) => setValue('isActive', v)}
            />
            <span className="text-sm text-muted-foreground">
              {isActiveVal ? 'Đang dùng' : 'Đã tắt'}
            </span>
          </div>
        </div>
      </div>

      {/* Note */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="note" className="text-sm font-medium">Ghi chú</Label>
        <Textarea
          id="note"
          rows={2}
          placeholder="Thiết bị, tầng..."
          {...register('note')}
        />
      </div>

      {/* Actions */}
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              {isEdit ? 'Đang lưu…' : 'Đang tạo…'}
            </span>
          ) : isEdit ? (
            'Lưu thay đổi'
          ) : (
            'Thêm phòng'
          )}
        </Button>
      </div>
    </form>
  );
}
